import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { assetPath } from '@/utils/assetPath'
import { useEffect, useState } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { isIOSApp } from '@/utils/platformDetection'
import { GlossyButton } from '@/components/shared/atoms/GlossyButton'
import { FadeInUp } from '@/components/shared/atoms/FadeInUp'
import { useOnboardingStore } from '@/stores/onboarding'
import platformSubscriptionsApi from '@wtf/shared/api/platformSubscriptions'
import { useQueryClient } from '@tanstack/react-query'
import { captureException } from '@/config/sentry'

const PLAN_INFO = {
    pro: {
        name: 'Pro',
        tryOns: 100,
    },
    max: {
        name: 'Max',
        tryOns: 500,
    },
}

export const Route = createFileRoute('/(onboarding)/(pricing)/checkout-return')(
    {
        validateSearch: (search: Record<string, unknown>) => ({
            session_id: search.session_id as string | undefined,
            plan: search.plan as 'pro' | 'max' | undefined,
            returnToPath:
                typeof search.returnToPath === 'string'
                    ? search.returnToPath
                    : undefined,
        }),
        component: CheckoutReturnRoute,
    }
)

function CheckoutReturnRoute() {
    const { session_id, plan, returnToPath } = Route.useSearch()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { setRegistrationData } = useOnboardingStore()

    const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
        'loading'
    )
    const [errorMessage, setErrorMessage] = useState<string>('')

    const planInfo = plan ? PLAN_INFO[plan] : null

    // TEMPORARY: Phase I iOS restriction - Remove in Phase II
    // Redirect iOS users away from checkout return page to comply with App Store guidelines
    // See: docs/PHASE_I_TEMPORARY_IOS_RESTRICTIONS.md
    useEffect(() => {
        if (isIOSApp()) {
            navigate({ to: '/' })
        }
    }, [navigate])

    // TEMPORARY: Phase I iOS restriction - Remove in Phase II
    // Don't render checkout return page on iOS while redirecting
    if (isIOSApp()) {
        return null
    }

    useEffect(() => {
        if (!session_id) {
            setStatus('error')
            setErrorMessage('No session ID provided')
            return
        }

        const checkSession = async () => {
            try {
                // Step 1: Verify the Stripe checkout session is complete
                const sessionStatus =
                    await platformSubscriptionsApi.getSessionStatus(session_id)

                if (sessionStatus.status === 'complete') {
                    // Step 2: Wait for webhook to process and upgrade the user
                    const upgraded = await pollForUpgrade()

                    if (upgraded) {
                        // Invalidate subscription query to get fresh data
                        await queryClient.invalidateQueries({
                            queryKey: ['platform-subscription'],
                        })

                        // Save plan selection
                        if (plan) {
                            setRegistrationData({ pricingPlan: plan })
                        }

                        setStatus('success')
                    } else {
                        // Timeout - webhook hasn't processed yet
                        setStatus('error')
                        setErrorMessage(
                            'Payment successful, but upgrade is taking longer than expected. Please refresh the page in a moment.'
                        )
                    }
                } else if (sessionStatus.status === 'open') {
                    // Payment incomplete, redirect back to checkout
                    navigate({
                        to: '/checkout',
                        search: { plan: plan || 'pro', returnToPath },
                    })
                } else {
                    setStatus('error')
                    setErrorMessage('Payment was not completed')
                }
            } catch (error) {
                console.error('Error checking session status:', error)
                captureException(error, { session_id, plan, action: 'verify_checkout_session' })
                setStatus('error')
                setErrorMessage('Failed to verify payment status')
            }
        }

        /**
         * Poll the subscription API to check if the webhook has processed
         * and upgraded the user to the expected plan.
         * Polls every 1 second for up to 10 seconds.
         */
        const pollForUpgrade = async (): Promise<boolean> => {
            const MAX_ATTEMPTS = 10
            const POLL_INTERVAL = 1000 // 1 second
            const expectedPlan = plan?.toUpperCase() as 'PRO' | 'MAX'

            for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
                try {
                    // Fetch current subscription from database
                    const subscription =
                        await platformSubscriptionsApi.getCurrentSubscription()

                    // Check if the plan has been upgraded
                    if (subscription.plan === expectedPlan) {
                        console.log(
                            `Upgrade confirmed after ${attempt} attempt(s)`
                        )
                        return true
                    }

                    // Not upgraded yet, wait before next attempt
                    if (attempt < MAX_ATTEMPTS) {
                        console.log(
                            `Upgrade not yet processed (attempt ${attempt}/${MAX_ATTEMPTS}), waiting...`
                        )
                        await new Promise((resolve) =>
                            setTimeout(resolve, POLL_INTERVAL)
                        )
                    }
                } catch (error) {
                    console.error(
                        `Error polling subscription (attempt ${attempt}):`,
                        error
                    )
                    // Continue polling even on error
                    if (attempt < MAX_ATTEMPTS) {
                        await new Promise((resolve) =>
                            setTimeout(resolve, POLL_INTERVAL)
                        )
                    }
                }
            }

            // Timeout - webhook hasn't processed within 10 seconds
            console.warn(
                'Webhook processing timeout - upgrade not confirmed within 10 seconds'
            )
            return false
        }

        checkSession()
    }, [
        session_id,
        plan,
        returnToPath,
        navigate,
        queryClient,
        setRegistrationData,
    ])

    const handleContinue = () => {
        if (returnToPath) {
            navigate({ to: returnToPath })
        } else {
            navigate({ to: '/' })
        }
    }

    if (status === 'loading') {
        return (
            <section className="relative z-10 flex h-screen flex-col">
                <div className="flex px-2 pt-3 md:justify-center">
                    <img
                        alt="logo-red"
                        src={assetPath('logo/logo-red.svg')}
                        className="h-[34px] w-[155px] md:mt-12 md:h-[100px] md:w-[440px]"
                    />
                </div>

                <div className="flex flex-1 flex-col items-center justify-center">
                    <Loader2 className="h-12 w-12 animate-spin text-white" />
                    <p className="mt-4 text-white/70">
                        Verifying your payment and activating your
                        subscription...
                    </p>
                    <p className="mt-2 text-sm text-white/50">
                        This may take a few seconds
                    </p>
                </div>
            </section>
        )
    }

    if (status === 'error') {
        const isTimeout = errorMessage.includes('taking longer than expected')

        return (
            <section className="relative z-10 flex h-screen flex-col">
                <div className="flex px-2 pt-3 md:justify-center">
                    <img
                        alt="logo-red"
                        src={assetPath('logo/logo-red.svg')}
                        className="h-[34px] w-[155px] md:mt-12 md:h-[100px] md:w-[440px]"
                    />
                </div>

                <div className="flex flex-1 flex-col items-center justify-center">
                    <div className="mx-auto max-w-md px-6 text-center">
                        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full border-4 border-red-500 bg-red-500/20 backdrop-blur-md">
                            <span className="text-3xl">✕</span>
                        </div>
                        <h1 className="font-sfpro text-28 mb-3 font-semibold text-white">
                            {isTimeout ? 'Processing...' : 'Payment Error'}
                        </h1>
                        <p className="mb-8 text-white/70">{errorMessage}</p>
                        <div className="flex flex-col gap-3">
                            {isTimeout && (
                                <GlossyButton
                                    onClick={() => window.location.reload()}
                                    className="w-full rounded-xl py-4 text-base"
                                >
                                    Refresh Page
                                </GlossyButton>
                            )}
                            <GlossyButton
                                onClick={() =>
                                    navigate({
                                        to: '/pricing',
                                        search: {
                                            returnToPath: undefined,
                                            reason: undefined,
                                            preselect: undefined,
                                        },
                                    })
                                }
                                className="w-full rounded-xl py-4 text-base"
                                variant={isTimeout ? 'ghost' : undefined}
                            >
                                Back to Pricing
                            </GlossyButton>
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    return (
        <section className="relative z-10 flex h-screen flex-col">
            <div className="flex px-2 pt-3 md:justify-center">
                <img
                    alt="logo-red"
                    src={assetPath('logo/logo-red.svg')}
                    className="h-[34px] w-[155px] md:mt-12 md:h-[100px] md:w-[440px]"
                />
            </div>

            <div className="flex flex-1 flex-col items-center justify-center">
                <FadeInUp delay={0.2}>
                    <div className="mx-auto max-w-md px-6 text-center">
                        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full border-4 border-white bg-white/20 backdrop-blur-md">
                            <Check
                                className="h-10 w-10 text-white"
                                strokeWidth={3}
                            />
                        </div>

                        <h1 className="font-sfpro text-28 mb-3 font-semibold text-white">
                            Welcome to {planInfo?.name || 'Pro'}!
                        </h1>

                        <p className="mb-6 text-white/70">
                            You now have {planInfo?.tryOns || 100} try-ons per
                            month. Go buck wild 🕺
                        </p>

                        <GlossyButton
                            onClick={handleContinue}
                            className="w-full rounded-xl py-4 text-base"
                        >
                            Continue
                        </GlossyButton>
                    </div>
                </FadeInUp>
            </div>
        </section>
    )
}
